import { useMemo } from 'react'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import type { QueryDef } from '@/hooks/useDefinedQuery'
import { getAllSealedProducts, getSealedProductPrices } from '@/services/api/sealed'

const STALE_24H = 1000 * 60 * 60 * 24

const sealedQueries = {
  allList: {
    queryKey: () => ['sealedProducts', 'allList'] as const,
    queryFn: getAllSealedProducts,
    staleTime: STALE_24H,
    auth: 'public',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAllSealedProducts>>>,
  priceHistory: {
    queryKey: (id: number, days: number) => ['sealedProductPrices', id, days] as const,
    queryFn: (id: number, days: number) => getSealedProductPrices(id, days),
    staleTime: STALE_24H,
    auth: 'public',
  } satisfies QueryDef<Awaited<ReturnType<typeof getSealedProductPrices>>, [number, number]>,
}

// Exported for usePrefetchBulkData.ts cross-file key reference
export { sealedQueries }

export function useSealedProducts() {
  const query = useDefinedQuery(sealedQueries.allList)
  return {
    ...query,
    data: query.data?.sealedProducts,
  }
}

export function useSealedProduct(id: number) {
  const { data: allProducts, isLoading, error } = useSealedProducts()
  const data = useMemo(() => {
    if (!allProducts) return undefined
    return allProducts.find((p) => p.id === id)
  }, [allProducts, id])
  return { data, isLoading, error }
}

export function useSealedProductPriceHistory(id: number | undefined, days: number = 30) {
  // `?? 0` is a placeholder key segment while disabled — the query never runs without an id.
  return useDefinedQuery(sealedQueries.priceHistory, id ?? 0, days, { enabled: !!id })
}
